import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { track, withUtm } from "@/lib/track";
import { useAgent } from "@/lib/agentContext";
import { useAuth } from "@/lib/auth";
import PublicNav from "@/components/PublicNav";
import ProjectCard, { formatPrice } from "@/components/ProjectCard";
import {
  MessageCircle, Phone, MapPin, FileText, ArrowRight, BadgeCheck,
  TrendingUp, Home, FileImage, ChevronRight, Mail, PlayCircle, X, Download, CheckCircle2, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Detect and produce an embed URL for a YouTube / Vimeo link. Returns null
// for anything else — the caller falls back to a native <video> element.
function getVideoEmbed(url) {
  if (!url) return null;
  const yt = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/i,
  );
  if (yt) {
    return { provider: "youtube", src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0` };
  }
  const vi = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vi) {
    return { provider: "vimeo", src: `https://player.vimeo.com/video/${vi[1]}?autoplay=1` };
  }
  return null;
}

export default function ProjectDetail() {
  const { id, slug } = useParams();
  const ctxAgent = useAgent();
  const { user } = useAuth();
  const navigate = useNavigate();
  const insideAgentLayout = !!ctxAgent;
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [agent, setAgent] = useState(ctxAgent || null);
  const [error, setError] = useState(null);
  // Media / brochure-request UI
  const [videoOpen, setVideoOpen] = useState(false);
  const [brochureOpen, setBrochureOpen] = useState(false);
  const [brochureForm, setBrochureForm] = useState({ name: "", phone: "", email: "" });
  const [brochureSubmitting, setBrochureSubmitting] = useState(false);
  const [brochureSuccess, setBrochureSuccess] = useState(null); // {brochure_url}
  const [claiming, setClaiming] = useState(false);

  const isAgentUser = user?.role === "agent";

  const handleClaim = async () => {
    if (!user) {
      // Preserve intent — bring visitor back to this project after login.
      navigate(`/login?next=${encodeURIComponent(`/project/${id}`)}`);
      return;
    }
    if (user.role !== "agent") {
      toast.error("Only agents can claim projects to their portal.");
      return;
    }
    setClaiming(true);
    try {
      const { data } = await api.post(`/listings/claim-master/${id}`);
      if (data?.already_claimed) {
        toast.success("Already in your portal — opening your Listings.");
      } else {
        toast.success("Project added to your portal.");
      }
      navigate("/dashboard/listings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim this project. Please try again.");
    } finally {
      setClaiming(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const projectReq = api.get(`/master-projects/${id}`);
        // If we already have agent from layout context, skip the second fetch
        const agentReq = (slug && !ctxAgent) ? api.get(`/agent/${slug}`) : Promise.resolve(null);
        const [{ data: projectData }, agentRes] = await Promise.all([projectReq, agentReq]);
        if (cancelled) return;
        setProject(projectData);
        if (agentRes) setAgent(agentRes.data.agent);
        const { data: peers } = await api.get(`/master-projects`, {
          params: { locality: projectData.locality_tag },
        });
        if (cancelled) return;
        setRelated(peers.filter((p) => p.project_id !== projectData.project_id).slice(0, 3));
      } catch {
        if (!cancelled) setError(slug ? "Project or agent not found." : "Project not found.");
      }
    })();
    return () => { cancelled = true; };
  }, [id, slug, ctxAgent]);

  // Fire a project_view event (agent context only)
  useEffect(() => {
    if (slug && id) {
      track({ event_type: "project_view", agent_slug: slug, project_id: id, source: "project_detail" });
    }
  }, [slug, id]);

  const agentBase = slug ? `/agent/${slug}` : null;
  const detailHrefFor = (pid) => (slug ? `${agentBase}/project/${pid}` : `/project/${pid}`);

  if (error) {
    return (
      <div className="min-h-screen bg-sg-paper">
        {!insideAgentLayout && (slug && agent ? <AgentTopBar agent={agent} /> : <PublicNav />)}
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="overline text-sg-gold-deep mb-3">404</div>
          <h1 className="font-display text-4xl text-sg-navy">{error}</h1>
          <Link to={slug ? agentBase : "/"} className="pill-outline inline-flex items-center gap-2 mt-8">
            Back to {slug ? "agent page" : "home"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }
  if (!project || (slug && !agent)) {
    return (
      <div className="min-h-screen bg-sg-paper">
        {!insideAgentLayout && <PublicNav />}
        <div className="min-h-[40vh] flex items-center justify-center text-sg-mute animate-pulse">Loading project…</div>
      </div>
    );
  }

  // Build the contact / WhatsApp deep-link — agent-specific when in agent context.
  const waNum = agent?.phone ? agent.phone.replace(/[^0-9]/g, "") : "919999999999";
  const waMsg = encodeURIComponent(
    agent
      ? `Hi ${agent.name}, I'm interested in ${project.project_name} (${project.locality_tag}, ${project.developer}). MahaCRITI ID: ${project.maharera_id}.`
      : `Hi, I'd like more details on ${project.project_name} (${project.locality_tag}, ${project.developer}). MahaCRITI ID: ${project.maharera_id}.`
  );
  const waHref = withUtm(
    `https://wa.me/${waNum}?text=${waMsg}`,
    agent ? { agent_slug: agent.slug, project_id: id, source: "project_detail" } : {}
  );
  const callHref = agent?.phone ? `tel:${agent.phone}` : "tel:+919999999999";

  const onWaClick = () => {
    if (slug) track({ event_type: "wa_click", agent_slug: slug, project_id: id, source: "project_detail" });
  };
  const onCallClick = () => {
    if (slug) track({ event_type: "call_click", agent_slug: slug, project_id: id, source: "project_detail" });
  };

  const submitBrochureRequest = async (e) => {
    e.preventDefault();
    if (brochureSubmitting) return;
    setBrochureSubmitting(true);
    try {
      const { data } = await api.post(`/master-project/${id}/brochure-request`, {
        name: brochureForm.name.trim(),
        phone: brochureForm.phone.trim(),
        email: brochureForm.email.trim() || null,
        agent_slug: slug || null,
      });
      setBrochureSuccess({ brochure_url: data.brochure_url });
      setBrochureForm({ name: "", phone: "", email: "" });
      if (slug) track({ event_type: "brochure_request", agent_slug: slug, project_id: id, source: "project_detail" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request. Please try again.");
    } finally {
      setBrochureSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sg-paper">
      {!insideAgentLayout && (slug && agent ? <AgentTopBar agent={agent} onWa={onWaClick} onCall={onCallClick} /> : <PublicNav />)}

      {/* Breadcrumbs */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 text-sm text-sg-mute flex items-center gap-2 flex-wrap" data-testid="breadcrumbs">
        {slug && agent ? (
          <Link to={agentBase} className="hover:text-sg-navy inline-flex items-center gap-1" data-testid="bc-agent">
            <Home className="w-3.5 h-3.5" /> {agent.name}
          </Link>
        ) : (
          <Link to="/" className="hover:text-sg-navy inline-flex items-center gap-1"><Home className="w-3.5 h-3.5" /> Home</Link>
        )}
        <ChevronRight className="w-3.5 h-3.5" />
        <span>{project.zone}</span>
        <ChevronRight className="w-3.5 h-3.5" />
        <span>{project.locality_tag}</span>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-sg-navy">{project.project_name}</span>
      </div>

      {/* Hero card */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 rounded-3xl overflow-hidden border border-sg-line">
          <div className="relative aspect-[4/3] lg:aspect-auto min-h-[320px]" data-testid="project-hero-image">
            <img
              src={
                (project.image_urls && project.image_urls[0])
                || project.video_poster_url
                || project.thumbnail_url
              }
              alt={project.project_name}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <span className="absolute top-5 left-5 chip chip-gold">NEW LAUNCH</span>
            <span className="absolute top-5 right-5 chip">{project.locality_tag}</span>
            {project.video_url && (
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                className="absolute bottom-5 left-5 inline-flex items-center gap-2 rounded-full bg-black/70 hover:bg-black text-white px-4 py-2 text-sm font-semibold backdrop-blur-sm transition-colors"
                data-testid="project-watch-video-btn"
              >
                <PlayCircle className="w-5 h-5" /> Watch Video
              </button>
            )}
          </div>
          <div className="bg-sg-navy text-white p-10 lg:p-12 relative">
            <div className="overline text-sg-gold mb-3">{project.developer}</div>
            <h1 className="font-display text-4xl lg:text-5xl leading-[1.05]" data-testid="project-name">
              {project.project_name}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="chip chip-gold inline-flex items-center gap-1" data-testid="project-rera-badge">
                <BadgeCheck className="w-3.5 h-3.5" /> MahaCRITI ID: {project.maharera_id}
              </span>
              <span className="chip"><MapPin className="w-3 h-3 mr-1" /> {project.locality_tag}, {project.zone}</span>
            </div>
            <div className="mt-8 border-t border-white/15 pt-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/55">Starting from</div>
              <div className="rupee text-5xl text-sg-gold mt-1" data-testid="project-price">{formatPrice(project.starting_price_lakhs)}</div>
              <div className="mt-2 text-sm text-white/75 font-mono">{(project.configurations || []).join(" · ")}</div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={waHref} target="_blank" rel="noreferrer" onClick={onWaClick} className="pill-solid inline-flex items-center gap-2" data-testid="project-inquire-wa">
                <MessageCircle className="w-4 h-4" /> Inquire via WhatsApp{agent ? ` with ${agent.name.split(" ")[0]}` : ""}
              </a>
              <a href={callHref} onClick={onCallClick} className="pill-outline inline-flex items-center gap-2 text-white border-white/40 hover:bg-white hover:text-sg-navy" data-testid="project-call">
                <Phone className="w-4 h-4" /> Schedule Site Visit
              </a>
              <button
                type="button"
                onClick={() => { setBrochureSuccess(null); setBrochureOpen(true); }}
                className="pill-outline inline-flex items-center gap-2 text-white border-white/40 hover:bg-white hover:text-sg-navy"
                data-testid="project-request-brochure-btn"
              >
                <FileText className="w-4 h-4" /> Request Brochure
              </button>
              {isAgentUser && (
                <button
                  type="button"
                  onClick={handleClaim}
                  disabled={claiming}
                  className="pill-solid inline-flex items-center gap-2 bg-sg-gold text-sg-navy hover:brightness-95 disabled:opacity-70"
                  data-testid="project-claim-hero-btn"
                >
                  {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {claiming ? "Adding…" : "Add to my Portal"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Gallery — image_urls[1:] rendered as small tiles below the hero */}
        {(project.image_urls || []).length > 1 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="project-gallery">
            {project.image_urls.slice(1).map((url, i) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noreferrer"
                className="relative aspect-square rounded-xl overflow-hidden border border-sg-line group"
                data-testid={`project-gallery-item-${i}`}
              >
                <img src={url} alt={`${project.project_name} view ${i + 2}`} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
              </a>
            ))}
          </div>
        )}
      </section>

      {/* Strip */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-8">
        <div className="bg-white border border-sg-line rounded-full text-center py-3 overline text-sg-navy/70">
          {agent
            ? `Presented by ${agent.name} · MahaCRITI Reg No: ${agent.maharera_number}`
            : `MahaCRITI-verified · Live inventory · ${project.locality_tag}, ${project.zone}`}
        </div>
      </div>

      {/* Detail grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Specs */}
        <div className="sg-card p-7 lg:col-span-2" data-testid="project-specs-card">
          <div className="overline text-sg-gold-deep mb-3">PROJECT DETAILS</div>
          <h2 className="font-display text-3xl text-sg-navy">
            What&apos;s on offer at <span className="font-serif-italic text-sg-gold-deep">{project.project_name}.</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-6 text-sm">
            <SpecRow k="Developer" v={project.developer} />
            <SpecRow k="Configurations" v={(project.configurations || []).join(" · ")} />
            <SpecRow k="Locality" v={project.locality_tag} />
            <SpecRow k="Corridor" v={project.zone} />
            <SpecRow k="Starting Price" v={formatPrice(project.starting_price_lakhs)} accent />
            <SpecRow k="MahaCRITI ID" v={project.maharera_id} mono />
          </div>

          {/* Brochure + floor plans — hidden when nothing is attached to the project */}
          {(project.brochure_url || (project.floor_plan_urls || []).length > 0) && (
            <div className="mt-8 border-t border-dashed border-sg-line pt-6" data-testid="project-documents">
              <div className="overline text-sg-mute mb-3">DOCUMENTS</div>
              <div className="flex flex-wrap gap-3">
                {project.brochure_url && (
                  <a href={project.brochure_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-sg-line bg-sg-cream-soft px-4 py-2 text-sm hover:bg-white transition-colors" data-testid="project-brochure-link">
                    <FileText className="w-4 h-4 text-sg-gold-deep" /> Brochure
                  </a>
                )}
                {(project.floor_plan_urls || []).map((url, i) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-sg-line bg-sg-cream-soft px-4 py-2 text-sm hover:bg-white transition-colors" data-testid={`project-floorplan-link-${i}`}>
                    <FileImage className="w-4 h-4 text-sg-gold-deep" /> Floor Plan {i + 1}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right rail */}
        <div className="space-y-6">
          {project.market_trend_data && (
            <div className="sg-card-cream" data-testid="project-trend-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white border border-sg-line flex items-center justify-center"><TrendingUp className="w-4 h-4 text-sg-gold-deep" /></div>
                <div>
                  <div className="overline text-sg-gold-deep">{project.locality_tag} TREND</div>
                  <div className="rupee text-3xl text-sg-navy mt-1">{project.market_trend_data.yoy}<span className="text-xs text-sg-mute ml-1 font-sans">YoY</span></div>
                </div>
              </div>
              <p className="text-sm text-sg-navy mt-4">{project.market_trend_data.summary}</p>
            </div>
          )}

          {/* Agent-context card vs marketing CTA */}
          {agent ? (
            <div className="sg-card-dark" data-testid="project-agent-profile-card">
              <div className="flex items-center gap-4">
                <img
                  src={agent.picture || "https://images.pexels.com/photos/7580994/pexels-photo-7580994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=160&w=160"}
                  alt={agent.name}
                  className="w-16 h-16 rounded-full object-cover border border-sg-gold/40"
                  data-testid="project-agent-avatar"
                />
                <div>
                  <div className="overline text-sg-gold">YOUR ADVISOR</div>
                  <h3 className="font-display text-xl mt-1">{agent.name}</h3>
                  <p className="text-xs text-white/65 mt-0.5">{agent.micro_market} Specialist · {agent.primary_locality}</p>
                </div>
              </div>
              <div className="mt-4">
                <span className="chip chip-gold inline-flex items-center gap-1">
                  <BadgeCheck className="w-3.5 h-3.5" /> MahaCRITI Reg No: {agent.maharera_number}
                </span>
              </div>
              {agent.bio && <p className="text-sm text-white/75 mt-4 line-clamp-4">{agent.bio}</p>}
              <div className="mt-5 flex flex-wrap gap-2">
                <a href={waHref} target="_blank" rel="noreferrer" onClick={onWaClick} className="pill-solid inline-flex items-center gap-2" data-testid="project-agent-wa">
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
                {agent.phone && (
                  <a href={callHref} onClick={onCallClick} className="pill-outline inline-flex items-center gap-2 text-white border-white/40 hover:bg-white hover:text-sg-navy" data-testid="project-agent-call">
                    <Phone className="w-4 h-4" /> Call
                  </a>
                )}
                <Link to={agentBase} className="pill-outline inline-flex items-center gap-2 text-white border-white/40 hover:bg-white hover:text-sg-navy" data-testid="project-agent-portfolio">
                  View Portfolio
                </Link>
              </div>
            </div>
          ) : (
            <div className="sg-card-dark" data-testid="project-agent-cta">
              <div className="overline text-sg-gold mb-2">FOR AGENTS</div>
              <h3 className="font-display text-2xl leading-tight">
                Feature this on your <span className="font-serif-italic text-sg-gold">branded portal.</span>
              </h3>
              <p className="text-sm text-white/75 mt-3">
                Onboard to your corridor and {project.project_name} auto-appears on your branded INPropPortal page.
              </p>
              {isAgentUser ? (
                <button
                  type="button"
                  onClick={handleClaim}
                  disabled={claiming}
                  className="pill-solid inline-flex items-center gap-2 mt-5 bg-sg-gold text-sg-navy hover:brightness-95 disabled:opacity-70"
                  data-testid="project-claim-cta-btn"
                >
                  {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {claiming ? "Adding to your Portal…" : "Add to my Portal"} <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <Link to="/login" className="pill-solid inline-flex items-center gap-2 mt-5" data-testid="project-onboard-cta">
                  Claim Your Portal <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          )}
        </div>
      </section>

      {/* -------- Authorized Partners -------- */}
      {(project.authorized_partners || []).length > 0 && (
        <section
          className="border-t border-sg-line bg-sg-cream-soft/40 py-14"
          data-testid="authorized-partners-section"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="overline text-sg-gold-deep mb-3">AVAILABLE THROUGH THESE AUTHORIZED PARTNERS</div>
            <h2 className="font-display text-3xl lg:text-4xl text-sg-navy max-w-2xl">
              {project.authorized_partners.length}
              <span className="font-serif-italic text-sg-gold-deep"> MahaCRITI-registered </span>
              {project.authorized_partners.length === 1 ? "agent handles" : "agents handle"} {project.project_name}.
            </h2>
            <p className="text-sm text-sg-mute mt-3 max-w-2xl">
              Contact any partner directly — each maintains their own branded portal on INPropPortal.
            </p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {project.authorized_partners.map((p) => (
                <Link
                  key={p.slug}
                  to={`/agent/${p.slug}`}
                  className="group flex items-center gap-4 bg-white border border-sg-line rounded-2xl p-4 hover:border-sg-gold hover:shadow-sm transition-all"
                  data-testid={`authorized-partner-${p.slug}`}
                >
                  {p.profile_picture ? (
                    <img
                      src={p.profile_picture}
                      alt={p.name}
                      className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-sg-navy text-sg-gold flex items-center justify-center text-lg font-semibold flex-shrink-0">
                      {(p.name || "?").slice(0, 1)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {p.is_primary && (
                      <div className="text-[9px] uppercase tracking-[0.18em] text-sg-gold-deep font-semibold mb-0.5">
                        Primary partner
                      </div>
                    )}
                    <div className="text-sm font-semibold text-sg-navy truncate group-hover:text-sg-gold-deep transition-colors">
                      {p.name}
                    </div>
                    {p.maharera_number && (
                      <div className="font-mono text-[10px] text-sg-mute truncate mt-0.5">
                        MahaCRITI · {p.maharera_number}
                      </div>
                    )}
                    {p.primary_locality && (
                      <div className="text-[11px] text-sg-mute mt-0.5 truncate">
                        {p.primary_locality}
                      </div>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-sg-mute group-hover:text-sg-gold-deep transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-sg-line bg-white py-14">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="overline text-sg-gold-deep mb-3">MORE IN {project.locality_tag.toUpperCase()}</div>
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
              <h2 className="font-display text-3xl lg:text-4xl text-sg-navy max-w-2xl">
                Other curated launches in <span className="font-serif-italic text-sg-gold-deep">{project.locality_tag}.</span>
              </h2>
              <Link to={slug ? agentBase : "/login"} className="pill-outline inline-flex items-center gap-2" data-testid="see-corridor-cta">
                {slug ? `Back to ${agent.name.split(" ")[0]}` : `See ${project.zone}`} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <ProjectCard
                  key={p.project_id}
                  project={p}
                  tag="NEW LAUNCH"
                  testIdPrefix="related-project"
                  detailHref={detailHrefFor(p.project_id)}
                  inquireHref={agent ? withUtm(`https://wa.me/${waNum}?text=${encodeURIComponent(`Hi ${agent.name}, I'm interested in ${p.project_name} (${p.locality_tag}).`)}`, { agent_slug: agent.slug, project_id: p.project_id, source: "project_detail" }) : undefined}
                  onInquire={agent ? () => track({ event_type: "wa_click", agent_slug: agent.slug, project_id: p.project_id, source: "project_detail" }) : undefined}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="py-10 border-t border-sg-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap items-center justify-between gap-3 text-xs text-sg-mute">
          <span>© 2026 INPropPortal · MahaCRITI-aligned</span>
          <span className="font-mono">
            {agent ? `${agent.name} · MahaCRITI Reg No: ${agent.maharera_number}` : `${project.project_name} · ${project.maharera_id}`}
          </span>
        </div>
      </footer>

      {/* -------- Video modal -------- */}
      {project.video_url && (() => {
        const embed = getVideoEmbed(project.video_url);
        return (
          <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
            <DialogContent className="max-w-5xl w-[95vw] bg-black border-none p-0 overflow-hidden" data-testid="project-video-modal">
              <button
                type="button"
                onClick={() => setVideoOpen(false)}
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black text-white flex items-center justify-center"
                aria-label="Close video"
                data-testid="project-video-close"
              >
                <X className="w-5 h-5" />
              </button>
              {videoOpen && (
                embed ? (
                  <div className="w-full bg-black" style={{ aspectRatio: "16 / 9" }}>
                    <iframe
                      src={embed.src}
                      title={`${project.project_name} video`}
                      className="w-full h-full max-h-[85vh]"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid={`project-video-iframe-${embed.provider}`}
                    />
                  </div>
                ) : (
                  <video
                    src={project.video_url}
                    className="w-full max-h-[85vh] bg-black"
                    controls
                    autoPlay
                    playsInline
                    data-testid="project-video-player"
                  />
                )
              )}
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* -------- Brochure request modal -------- */}
      <Dialog open={brochureOpen} onOpenChange={(open) => { setBrochureOpen(open); if (!open) setBrochureSuccess(null); }}>
        <DialogContent className="max-w-md bg-white p-0 overflow-hidden" data-testid="brochure-request-modal">
          {!brochureSuccess ? (
            <form
              onSubmit={submitBrochureRequest}
              className="p-6 space-y-4"
              data-testid="brochure-request-form"
            >
              <div>
                <div className="overline text-sg-gold-deep">REQUEST BROCHURE</div>
                <h2 className="font-display text-2xl text-sg-navy mt-1">
                  Get the {project.project_name} brochure.
                </h2>
                <p className="text-sm text-sg-mute mt-1">Enter your details — our team will follow up with the brochure and site-visit options.</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Full name</label>
                <Input
                  value={brochureForm.name}
                  onChange={(e) => setBrochureForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Priya Sharma"
                  className="mt-2 rounded-xl bg-white border-sg-line"
                  data-testid="brochure-name-input"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Phone number</label>
                <Input
                  value={brochureForm.phone}
                  onChange={(e) => setBrochureForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="+91 98765 43210"
                  className="mt-2 rounded-xl bg-white border-sg-line"
                  data-testid="brochure-phone-input"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Email <span className="text-sg-mute font-normal normal-case">(optional)</span></label>
                <Input
                  type="email"
                  value={brochureForm.email}
                  onChange={(e) => setBrochureForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="you@example.com"
                  className="mt-2 rounded-xl bg-white border-sg-line"
                  data-testid="brochure-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={brochureSubmitting}
                className="w-full rounded-full h-11 bg-sg-navy hover:bg-sg-navy-700 text-white disabled:opacity-50"
                data-testid="brochure-submit"
              >
                {brochureSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {brochureSubmitting ? "Sending…" : "Request brochure"}
              </Button>
            </form>
          ) : (
            <div className="p-8 text-center" data-testid="brochure-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h2 className="font-display text-2xl text-sg-navy mt-4">Thank you!</h2>
              <p className="text-sm text-sg-mute mt-2">
                Our team will contact you shortly about {project.project_name}.
              </p>
              {brochureSuccess.brochure_url ? (
                <a
                  href={brochureSuccess.brochure_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-sg-gold-deep hover:bg-sg-gold text-white px-5 py-2.5 text-sm font-semibold"
                  data-testid="brochure-download-now"
                >
                  <Download className="w-4 h-4" /> Download brochure now
                </a>
              ) : (
                <div className="mt-6 text-xs text-sg-mute italic">
                  The brochure will be shared with you personally by our team.
                </div>
              )}
              <button
                type="button"
                onClick={() => { setBrochureOpen(false); setBrochureSuccess(null); }}
                className="mt-6 block mx-auto text-xs text-sg-mute hover:text-sg-navy underline underline-offset-2"
                data-testid="brochure-close-thanks"
              >
                Close
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SpecRow({ k, v, accent, mono }) {
  return (
    <div className="flex justify-between border-b border-dashed border-sg-line py-2">
      <span className="text-sg-mute">{k}</span>
      <span className={`${accent ? "text-sg-gold-deep" : "text-sg-navy"} ${mono ? "font-mono" : ""} font-medium text-right`}>{v}</span>
    </div>
  );
}

/** Slim dark top bar surfacing the agent identity while inside the micro-site context. */
function AgentTopBar({ agent, onWa, onCall }) {
  const waNum = agent?.phone ? agent.phone.replace(/[^0-9]/g, "") : "";
  const waHref = waNum ? `https://wa.me/${waNum}` : null;
  return (
    <header className="bg-sg-navy text-white" data-testid="agent-context-bar">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        <Link to={`/agent/${agent.slug}`} className="flex items-center gap-3 min-w-0" data-testid="agent-context-back">
          <img
            src={agent.picture || "https://images.pexels.com/photos/7580994/pexels-photo-7580994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=120&w=120"}
            alt={agent.name}
            className="w-10 h-10 rounded-full object-cover border border-sg-gold/40 shrink-0"
          />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">Presented by</div>
            <div className="font-display text-lg truncate">{agent.name}</div>
          </div>
          <span className="hidden md:inline chip chip-gold ml-2">MahaCRITI Reg No: {agent.maharera_number}</span>
        </Link>
        <div className="hidden md:flex items-center gap-2">
          {waHref && (
            <a href={waHref} target="_blank" rel="noreferrer" onClick={onWa} className="pill-solid inline-flex items-center gap-2" data-testid="agent-context-wa">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          )}
          {agent.phone && (
            <a href={`tel:${agent.phone}`} onClick={onCall} className="pill-outline text-white border-white/40 hover:bg-white hover:text-sg-navy inline-flex items-center gap-2" data-testid="agent-context-call">
              <Phone className="w-4 h-4" /> Call
            </a>
          )}
          <a href="mailto:" className="pill-outline text-white border-white/40 hover:bg-white hover:text-sg-navy inline-flex items-center gap-2" data-testid="agent-context-email">
            <Mail className="w-4 h-4" /> Email
          </a>
        </div>
      </div>
    </header>
  );
}
